/**
 * HoCostsTab - Costs & Payments tab for active project detail.
 * Matched exactly to extracted_assets_2: bf832ac1-9d48-45db-a651-9a4805854e40
 */

import * as React from "react";
import { SkeletonRows } from "@/components/ui/skeleton";
import { StudioButton } from "@/features/studio/theme";
import { useProjectFunding } from "../../../hooks/use-project-funding";
import {
	useProjectMilestones,
	useProjectPayments,
} from "../../../hooks/use-projects";
import { getProjectBOQs } from "../../../services/boq/boq-api.service";
import {
	type CostEstimate,
	type EscrowTransaction,
	getProjectCosts,
	type Milestone,
} from "../../../services/projects-api.service";
import {
	AWAITING_PAYOUT_LABEL,
	DISPUTE_PAYOUT_FAILED_LABEL,
	DISPUTE_PAYOUT_FAILED_NOTE,
	DUPLICATE_CHARGE_LABEL,
	DUPLICATE_CHARGE_NOTE,
	DUPLICATE_CHARGE_REFUNDED_NOTE,
	FUNDS_ADDED_TITLE,
	isContractorAssignment,
	isDisputePayoutFailed,
	isDuplicateCharge,
	isEscrowTopUp,
	isSplitPartPaid,
	SPLIT_PART_PAID_LABEL,
	splitRefundNote,
	TOP_UP_HELD_LABEL,
} from "../../../utils/escrow-balance";
import {
	canConfirmCompletion,
	confirmCompletionAndRelease,
} from "../confirm-completion";
import {
	Btn,
	Eyebrow,
	FloatPanel,
	formatINR as fmt,
	Icn,
	ItalicAccent,
	PILL_TONES,
	Pill,
	paiseToRupees,
	secondaryBtnStyle,
} from "../design-primitives";

// One entry per member of `EscrowTransaction["status"]`, keyed by the status
// itself. Collapsing several statuses onto one key is what put "Released" on a
// `captured` row (money charged and sitting in escrow, released to nobody) and
// on a `refunded` one (money returned to the homeowner).
//
// "Awaiting your release" belongs to `held` alone: it says money is in escrow,
// and `held` is the only status the Release button below acts on. It overstates
// two kinds of row the homeowner cannot release, both written with no
// milestone, so each is given its own label below instead: a top-up at `held`
// `TOP_UP_HELD_LABEL`, and a contractor assignment, written at `held` by
// `hireContractor` and paid out by `authorizeAssignmentPayment`,
// `AWAITING_PAYOUT_LABEL`. A milestone's row a dispute split has part paid is
// `held` too, and gets `SPLIT_PART_PAID_LABEL`, and one whose dispute
// resolution's payout failed gets `DISPUTE_PAYOUT_FAILED_LABEL`, which takes
// precedence over it. `pending` gets "Awaiting payment",
// which claims only that the money has not arrived -- true both before the
// homeowner opens checkout and in the gap between paying and the provider
// settling the row. `captured` is "Funded", the same word `ho-milestones-tab`
// uses for it.
//
// A funding row -- a milestone's, or a top-up -- is written at `pending` and
// moved to `held` by `finalizeProjectFunding`; a contractor assignment is
// written at `held` outright by `hireContractor` in
// `project-contractors.service.ts`. A payout moves a row to `released`, and an
// admin dispute resolution to `refunded`/`partially_refunded`.
//
// `failed` never reaches this list: `getProjectPayments` leaves `failed` rows
// out (they are checkouts `supersedeStrandedSession` closed, not payments).
// Its entries keep the maps exhaustive.
const PAY_TONE: Record<EscrowTransaction["status"], keyof typeof PILL_TONES> = {
	pending: "risk",
	captured: "awaiting",
	held: "awaiting",
	released: "done",
	refunded: "neutral",
	partially_refunded: "neutral",
	failed: "risk",
};
const PAY_LABEL: Record<EscrowTransaction["status"], string> = {
	pending: "Awaiting payment",
	captured: "Funded",
	held: "Awaiting your release",
	released: "Released",
	refunded: "Refunded",
	partially_refunded: "Partially refunded",
	failed: "Payment failed",
};

const COST_CATEGORY_LABEL: Record<CostEstimate["category"], string> = {
	design_fees: "Design Fees",
	labor: "Labor",
	materials: "Materials",
	miscellaneous: "Miscellaneous",
};

function formatTransactionDate(dateStr: string): string {
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-IN", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

interface HoCostsTabProps {
	projectId: string;
	accent?: string;
	designerStudio?: string;
}

export function HoCostsTab({
	projectId,
	accent = "var(--color-dashboard-accent)",
	designerStudio,
}: HoCostsTabProps) {
	const {
		data: paymentsData,
		isLoading: paymentsLoading,
		error: paymentsError,
		refetch: refetchPayments,
	} = useProjectPayments(projectId);
	const { isFunding, fundMilestones } = useProjectFunding();
	// Each held row's milestone status decides what its button does: a
	// milestone not yet completed has to be confirmed before it can be paid.
	const { data: milestones, refetch: refetchMilestones } =
		useProjectMilestones(projectId);
	const milestoneById = new Map((milestones ?? []).map((m) => [m.id, m]));
	const [_boqsError, setBoqsError] = React.useState(false);

	const transactions: EscrowTransaction[] = paymentsData?.transactions ?? [];

	// Every milestone an open funding order covers. `fundProject` resumes such an
	// order only when the request names exactly its milestones, and refuses any
	// other set — including one milestone of a larger order. A `pending` row was
	// written by that order, so a row with a milestone is part of one, and the
	// order is reopened as a whole from this tab's rows (see `handleFundEscrow`).
	const inFlightMilestoneIds = React.useMemo(
		() =>
			Array.from(
				new Set(
					transactions
						.filter((txn) => txn.status === "pending" && txn.milestoneId)
						.map((txn) => txn.milestoneId as string),
				),
			),
		[transactions],
	);

	// What that order is for. Its rows carry its amount — one per milestone it
	// covers — so their sum is what the checkout will ask for, which is what the
	// row's own button must not be read as.
	const inFlightTotal = React.useMemo(
		() =>
			transactions
				.filter((txn) => txn.status === "pending" && txn.milestoneId)
				.reduce((sum, txn) => sum + txn.amount, 0),
		[transactions],
	);
	const summary = paymentsData?.summary ?? {
		totalFunded: 0,
		totalHeld: 0,
		totalReleased: 0,
	};

	const [loadingId, setLoadingId] = React.useState<string | null>(null);

	const [boqTotal, setBoqTotal] = React.useState<number | null>(null);
	const [designerCosts, setDesignerCosts] = React.useState<CostEstimate[]>([]);

	React.useEffect(() => {
		let cancelled = false;
		async function fetchDesignerCosts() {
			const costs = await getProjectCosts(projectId);
			if (!cancelled) setDesignerCosts(costs);
		}
		fetchDesignerCosts();
		return () => {
			cancelled = true;
		};
	}, [projectId]);

	React.useEffect(() => {
		let cancelled = false;
		async function fetchBoqTotal() {
			try {
				const result = await getProjectBOQs(projectId);
				if (result.error) {
					if (!cancelled) setBoqsError(true);
					return;
				}
				const boqs = result.data;
				if (!cancelled) {
					const sentBocs = boqs.filter(
						(boq: { status?: string }) =>
							boq.status === "sent" || boq.status === "approved",
					);
					// `grandTotal` is paise; everything it is compared with on this
					// tab (released escrow, designer estimates) is rupees.
					const total = paiseToRupees(
						sentBocs.reduce((sum, boq) => sum + (boq.grandTotal || 0), 0),
					);
					setBoqTotal(total > 0 ? total : null);
				}
			} catch {
				if (!cancelled) setBoqsError(true);
			}
		}
		fetchBoqTotal();
		return () => {
			cancelled = true;
		};
	}, [projectId]);

	const designerCostsTotal = designerCosts.reduce(
		(sum, cost) => sum + cost.estimatedAmount,
		0,
	);
	const totalEst = boqTotal ?? designerCostsTotal;
	const totalRemaining = Math.max(0, totalEst - summary.totalReleased);
	const hasBoqTotal = boqTotal !== null && boqTotal > 0;
	const hasDesignerCosts = designerCosts.length > 0 && designerCostsTotal > 0;

	const summaryCards = [
		...(hasBoqTotal
			? [
					{
						label: "ESTIMATED (BOQ)",
						value: totalEst,
						color: "var(--color-dashboard-text)",
					},
				]
			: []),
		...(hasDesignerCosts
			? [
					{
						label: "DESIGNER ESTIMATE",
						value: designerCostsTotal,
						color: "var(--color-dashboard-text)",
					},
				]
			: []),
		{
			label: "SPENT SO FAR",
			value: summary.totalReleased,
			color: accent,
		},
		{
			label: "REMAINING",
			value: totalRemaining,
			color: "var(--color-dashboard-success)",
		},
	];

	// Funding is a checkout, not a single call. `fundProject` alone only mints a
	// provider order, so the orchestration opens that order's checkout, verifies
	// it, and toasts each branch itself — nothing is reported here. What the row
	// then reads as comes back from `refetchPayments`, which the orchestration
	// runs only on a payment the server confirmed.
	//
	// `milestoneId` is required rather than read off the row: an empty milestone
	// list means "every unfunded milestone in this project" to the backend, so a
	// row whose milestone is gone would open a checkout for the whole remaining
	// project value against a button showing its own amount. The button is not
	// rendered for such a row (see below), and this signature is why that cannot
	// be undone by accident.
	//
	// The request names every milestone the open order covers, not the row's own:
	// `fundProject` resumes an open order only for a request that matches it
	// exactly, so asking for one milestone of a two-milestone order is refused
	// outright -- with "a payment is already in progress", which offered the
	// reader nothing to do about it. Reopening the order as a whole is the one
	// request it accepts, and it is the same payment either way.
	async function handleFundEscrow(txn: EscrowTransaction, milestoneId: string) {
		setLoadingId(txn.id);
		try {
			await fundMilestones({
				projectId,
				milestoneIds: inFlightMilestoneIds.length
					? inFlightMilestoneIds
					: [milestoneId],
				onFunded: async () => {
					await refetchPayments();
				},
			});
		} finally {
			setLoadingId(null);
		}
	}

	// `milestoneId` is a parameter rather than read off the row for the same
	// reason as `handleFundEscrow` above: the release is addressed by
	// milestone, and a row that does not name one has nothing this function can
	// release. The button is not rendered for such a row (see below), so this
	// signature is what stops that being undone by accident.
	//
	// The same action as the Milestones tab's "Confirm completion": the
	// backend's release refuses a milestone that is not completed, so for one
	// in progress this marks it completed first.
	async function handleRelease(txn: EscrowTransaction, milestone: Milestone) {
		setLoadingId(txn.id);
		try {
			await confirmCompletionAndRelease(milestone);
		} finally {
			// Both, whatever happened: a failed release can still have marked the
			// milestone completed, which changes this row's button.
			await Promise.all([refetchPayments(), refetchMilestones()]);
			setLoadingId(null);
		}
	}

	if (paymentsLoading) {
		return (
			<SkeletonRows
				label="Loading payments..."
				className="p-10"
				rowClassName="h-16 rounded-xl"
			/>
		);
	}

	if (paymentsError) {
		return (
			<div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
				<div
					style={{
						color: "var(--color-dashboard-pill-risk)",
						fontFamily: "var(--font-body)",
					}}
				>
					Failed to load payments
				</div>
			</div>
		);
	}

	return (
		<div>
			{/* Header */}
			<div style={{ marginBottom: 20 }}>
				<Eyebrow>WHERE YOUR BUDGET GOES · ROOM-WISE</Eyebrow>
				<h2
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 30,
						letterSpacing: "-0.02em",
						color: "var(--color-dashboard-text)",
						margin: "8px 0 0",
					}}
				>
					Every rupee,{" "}
					<ItalicAccent size={30} color={accent}>
						accounted for
					</ItalicAccent>
				</h2>
			</div>

			{/* Summary cards */}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: `repeat(${summaryCards.length}, 1fr)`,
					gap: 16,
					marginBottom: 24,
				}}
			>
				{summaryCards.map((card) => (
					<FloatPanel key={card.label} elev={2} padding={22}>
						<Eyebrow style={{ fontSize: 9 }}>{card.label}</Eyebrow>
						<div
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 30,
								color: card.color,
								letterSpacing: "-0.02em",
								marginTop: 8,
								fontVariantNumeric: "tabular-nums",
							}}
						>
							{fmt(card.value)}
						</div>
					</FloatPanel>
				))}
			</div>

			{/* Designer-added cost estimates */}
			{hasDesignerCosts && (
				<div>
					<div style={{ marginBottom: 14 }}>
						<Eyebrow>COSTS ADDED · BY YOUR DESIGNER</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 24,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "8px 0 0",
							}}
						>
							Line items,{" "}
							<ItalicAccent size={24} color={accent}>
								as shared
							</ItalicAccent>
						</h2>
					</div>
					<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
						{designerCosts.map((cost) => (
							<FloatPanel key={cost.id} elev={1} padding={0}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 16,
										padding: "16px 22px",
										flexWrap: "wrap",
									}}
								>
									<Icn
										name="wallet"
										size={15}
										color={accent}
										style={{ flexShrink: 0 }}
									/>
									<div style={{ flex: 1, minWidth: 200 }}>
										<div
											style={{
												fontFamily: "var(--font-body)",
												fontSize: 14.5,
												fontWeight: 600,
												color: "var(--color-dashboard-text)",
											}}
										>
											{cost.description}
										</div>
										<div
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 8.5,
												letterSpacing: "0.08em",
												color: "var(--color-dashboard-muted)",
												marginTop: 3,
												textTransform: "uppercase",
											}}
										>
											{COST_CATEGORY_LABEL[cost.category]} · added{" "}
											{formatTransactionDate(cost.createdAt)}
										</div>
									</div>
									<div style={{ textAlign: "right", flexShrink: 0 }}>
										<div
											style={{
												fontFamily: "var(--font-display)",
												fontWeight: 500,
												fontSize: 22,
												color: "var(--color-dashboard-text)",
												fontVariantNumeric: "tabular-nums",
												letterSpacing: "-0.015em",
											}}
										>
											{fmt(cost.estimatedAmount)}
										</div>
										<Pill
											tone={cost.actualAmount != null ? "done" : "awaiting"}
											dot={false}
											style={{ marginTop: 5 }}
										>
											{cost.actualAmount != null
												? `Actual ${fmt(cost.actualAmount)}`
												: "Estimate"}
										</Pill>
									</div>
								</div>
							</FloatPanel>
						))}
					</div>
				</div>
			)}

			{/* Escrow / Payments section */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-end",
					margin: "40px 0 14px",
					flexWrap: "wrap",
					gap: 16,
				}}
			>
				<div>
					<Eyebrow>PROJECT PAYMENTS · ESCROW</Eyebrow>
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 30,
							letterSpacing: "-0.02em",
							color: "var(--color-dashboard-text)",
							margin: "8px 0 0",
						}}
					>
						Released &amp;{" "}
						<ItalicAccent size={30} color={accent}>
							held
						</ItalicAccent>
					</h2>
				</div>
			</div>

			{/* Info banner */}
			<div
				style={{
					display: "flex",
					gap: 10,
					padding: "13px 18px",
					background: "var(--color-dashboard-surface)",
					borderRadius: 8,
					marginBottom: 16,
					alignItems: "flex-start",
				}}
			>
				<Icn
					name="info"
					size={15}
					color={accent}
					style={{ marginTop: 1, flexShrink: 0 }}
				/>
				<span
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 12.5,
						lineHeight: 1.5,
						color: "var(--color-dashboard-muted-dark)",
					}}
				>
					Every rupee sits in Finishd escrow until you release it. Release only
					what you're happy with.
				</span>
			</div>

			{/* Payment list */}
			<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
				{transactions.length === 0 ? (
					<FloatPanel elev={1} padding={40}>
						<div
							style={{
								textAlign: "center",
								color: "var(--color-dashboard-muted)",
								fontFamily: "var(--font-body)",
							}}
						>
							No payments yet
						</div>
					</FloatPanel>
				) : (
					transactions.map((txn) => {
						// `??` rather than a defaulted map. The maps stay total on
						// `EscrowTransaction["status"]`, so adding a member to that union is
						// a build error at both of them and at both of `ho-milestones-tab`'s.
						// These guards cover the other direction: `status` arrives as JSON,
						// and the union is this frontend's assumption about the server, not a
						// promise from it. A member added to `escrowStatusEnum` and written by
						// some backend path, or a bundle older than the deploy serving it,
						// delivers a status no entry matches -- and `PILL_TONES[undefined]`
						// is undefined, so the status dot's `pt.dot` throws, through to the
						// only ErrorBoundary above it (the root one in `App.tsx`), which
						// replaces the whole app with "Something went wrong".
						//
						// The label falls back to the raw status rather than to a chosen
						// word, deliberately. A default of "Pending" or "Awaiting payment" is
						// exactly the collapsing these maps replaced: it asserts a location
						// for the homeowner's money on a status nobody has decided about,
						// which is how "Released" came to sit on a `refunded` row. A raw
						// status reads as unfinished and claims nothing.
						const tone = PAY_TONE[txn.status] ?? "neutral";
						const pt = PILL_TONES[tone];
						const topUp = isEscrowTopUp(txn);
						const payoutFailed = isDisputePayoutFailed(txn);
						const splitPartPaid = isSplitPartPaid(txn);
						// A held duplicate is labelled as one; once refunded it keeps the
						// "Refunded" label and gets a note saying what it was.
						const duplicateOf = isDuplicateCharge(txn);
						const duplicate = duplicateOf && txn.status === "held";
						const label = duplicate
							? DUPLICATE_CHARGE_LABEL
							: payoutFailed
								? DISPUTE_PAYOUT_FAILED_LABEL
								: splitPartPaid
									? SPLIT_PART_PAID_LABEL
									: topUp && txn.status === "held"
										? TOP_UP_HELD_LABEL
										: isContractorAssignment(txn) && txn.status === "held"
											? AWAITING_PAYOUT_LABEL
											: (PAY_LABEL[txn.status] ?? txn.status);
						const isLoading = loadingId === txn.id;

						// A row can be funded only if it still names the milestone the
						// payment is for. `escrowTransactions.milestoneId` is nullable with
						// `onDelete: "set null"`, so a deleted milestone leaves a row that
						// no funding call can express. A `pending` top-up names none either;
						// the Team tab's Add funds, given the same amount, resumes its
						// order. Held as a `const` so the narrowing survives into the click
						// handler's closure.
						const fundableMilestoneId =
							txn.status === "pending" ? txn.milestoneId : undefined;

						// The same test for the Release button. `releaseMilestonePayment` is
						// addressed by milestone, so a `held` row can be released only if it
						// still names one, and three kinds of row do not: one whose
						// milestone was deleted (`onDelete: "set null"`); a contractor
						// assignment, which `hireContractor` writes at `held` with
						// `milestoneId: null` on the homeowner's own project; and a top-up,
						// which `topUpEscrow` writes with no milestone. Gating on the status
						// alone would offer a button on each that could not reach the
						// backend at all. A row a dispute split has part paid, or whose
						// dispute resolution's payout failed, names its milestone, but
						// that milestone is under dispute, which `releaseMilestonePayment`
						// refuses. Beyond that, the row gets the button exactly when the
						// Milestones tab would offer "Confirm completion" on its milestone
						// (`canConfirmCompletion`), which also keeps it off a milestone
						// whose milestones have not loaded yet. A duplicate charge never
						// gets it: it is refunded, never released.
						const rowMilestone = txn.milestoneId
							? milestoneById.get(txn.milestoneId)
							: undefined;
						const releasableMilestone =
							txn.status === "held" &&
							!splitPartPaid &&
							!payoutFailed &&
							!duplicateOf &&
							rowMilestone &&
							canConfirmCompletion(rowMilestone)
								? rowMilestone
								: undefined;

						// Gated on the release condition rather than on `held`, because it
						// describes what pressing Release does. On a contractor assignment it
						// is false twice over: the money is held for the contractor, not the
						// studio, and no confirmation of the homeowner's releases it. On a
						// top-up it is false too: that money is for hiring contractors.
						const note = duplicate
							? DUPLICATE_CHARGE_NOTE
							: duplicateOf
								? DUPLICATE_CHARGE_REFUNDED_NOTE
								: payoutFailed
									? DISPUTE_PAYOUT_FAILED_NOTE
									: splitPartPaid
										? `${fmt(txn.releasedAmount ?? 0)} of this payment has been paid to your designer under a dispute resolution. ${splitRefundNote(txn)}`
										: txn.status === "pending" &&
												inFlightMilestoneIds.length > 1
											? `This payment is open with the payment provider for ${fmt(inFlightTotal)}, covering ${inFlightMilestoneIds.length} milestones in one checkout. Resume it to reopen that checkout.`
											: releasableMilestone && designerStudio
												? `Funded into escrow. Releases to ${designerStudio} once you confirm the work is done.`
												: undefined;

						return (
							<FloatPanel key={txn.id} elev={1} padding={0}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 16,
										padding: "18px 22px",
										flexWrap: "wrap",
									}}
								>
									{/* Status dot */}
									<span
										style={{
											width: 10,
											height: 10,
											borderRadius: "50%",
											background: pt.dot,
											flexShrink: 0,
										}}
									/>

									{/* Info */}
									<div style={{ flex: 1, minWidth: 200 }}>
										<div
											style={{
												fontFamily: "var(--font-body)",
												fontSize: 14.5,
												fontWeight: 600,
												color: "var(--color-dashboard-text)",
											}}
										>
											{topUp
												? FUNDS_ADDED_TITLE
												: txn.milestoneTitle || "Project Payment"}
										</div>
										<div
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 8.5,
												letterSpacing: "0.08em",
												color: "var(--color-dashboard-muted)",
												marginTop: 3,
												textTransform: "uppercase",
											}}
										>
											Bank transfer · {formatTransactionDate(txn.createdAt)}
										</div>
										{note && (
											<div
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 12,
													// `note` is set only on a `held` row: a releasable one,
													// money safely in escrow; one a dispute split has part
													// paid, whose refund is awaited or needs support; or one
													// whose dispute resolution's payout needs support. It
													// takes the same amber as that row's pill.
													color: "var(--color-dashboard-pill-awaiting)",
													marginTop: 6,
													lineHeight: 1.45,
													maxWidth: 520,
												}}
											>
												{note}
											</div>
										)}
									</div>

									{/* Amount */}
									<div style={{ textAlign: "right", flexShrink: 0 }}>
										<div
											style={{
												fontFamily: "var(--font-display)",
												fontWeight: 500,
												fontSize: 22,
												color: "var(--color-dashboard-text)",
												fontVariantNumeric: "tabular-nums",
												letterSpacing: "-0.015em",
											}}
										>
											{fmt(txn.amount)}
										</div>
										<Pill tone={tone} dot={false} style={{ marginTop: 5 }}>
											{label}
										</Pill>
									</div>

									{/* Release / Fund buttons. A `held` row's money is already in
									    escrow, so the action on it is releasing. A `pending` row
									    has been charged for nothing, so the action on it is
									    paying. Wired the other way round, each button called a
									    backend that rejects it: `releaseMilestonePayment`
									    requires `held` and `fundProject` refuses an
									    already-funded milestone. Each also needs the row to name
									    its milestone, so each renders off the id computed above
									    rather than off the status alone. Neither renders on any
									    other status: there is no homeowner action on a
									    `captured`, `released` or refunded row. */}
									{releasableMilestone && (
										<Btn
											variant="primary"
											size="sm"
											icon={<Icn name="check" size={13} color="white" />}
											style={{ flexShrink: 0, opacity: isLoading ? 0.7 : 1 }}
											onClick={() =>
												!isLoading && handleRelease(txn, releasableMilestone)
											}
										>
											{isLoading
												? "Releasing..."
												: releasableMilestone.status === "completed"
													? "Release"
													: "Confirm completion"}
										</Btn>
									)}
									{fundableMilestoneId && (
										<StudioButton
											variant="secondary"
											size="sm"
											style={{
												...secondaryBtnStyle("sm"),
												flexShrink: 0,
												opacity: isLoading ? 0.7 : 1,
											}}
											disabled={isFunding}
											onClick={() =>
												!isLoading && handleFundEscrow(txn, fundableMilestoneId)
											}
										>
											<Icn name="wallet" size={13} />
											{isLoading
												? "Funding..."
												: inFlightMilestoneIds.length > 1
													? "Resume payment"
													: "Fund escrow"}
										</StudioButton>
									)}
								</div>
							</FloatPanel>
						);
					})
				)}
			</div>
		</div>
	);
}
